import { useFrame, useThree } from "@react-three/fiber";
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { createBodyPart, getTexture } from "../../../utils/steve3D";
import {
  HEAD_ANGLE_DISTANCE_OFFSET,
  HEAD_OFFSET,
  HEAD_SCALE,
  PLAYER_DISTANCE,
  PLAYER_OFFSET_X,
} from "../constants";

export default function SteveHead({ cursorPosition, texture }) {
  const playerRef = useRef();
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(0, 0, PLAYER_DISTANCE);
    camera.lookAt(0, 0, 0);

    const player = new THREE.Group();

    // Head (Base)
    player.add(
      createBodyPart(
        8 / 64,
        8 / 64,
        8 / 64,
        [
          getTexture(texture, 16, 8, 8, 8), // Right
          getTexture(texture, 0, 8, 8, 8), // Left
          getTexture(texture, 8, 0, 8, 8), // Top
          getTexture(texture, 16, 0, 8, 8), // Bottom
          getTexture(texture, 8, 8, 8, 8), // Front
          getTexture(texture, 24, 8, 8, 8), // Back
        ],
        0,
        28 / 64,
        0
      )
    );

    // Head (Hat / Outer Layer)
    player.add(
      createBodyPart(
        8.6 / 64,
        8.6 / 64,
        8.6 / 64,
        [
          getTexture(texture, 48, 8, 8, 8, true), // Right
          getTexture(texture, 32, 8, 8, 8, true), // Left
          getTexture(texture, 40, 0, 8, 8, true), // Top
          getTexture(texture, 48, 0, 8, 8, true), // Bottom
          getTexture(texture, 40, 8, 8, 8, true), // Front
          getTexture(texture, 56, 8, 8, 8, true), // Back
        ],
        0,
        28 / 64,
        0
      )
    );

    const playerContainer = playerRef.current;
    playerContainer.add(player);

    // Scale the entire player
    player.scale.setScalar(HEAD_SCALE);
    player.translateY(HEAD_OFFSET);
    player.position.x = PLAYER_OFFSET_X;

    return () => {
      playerContainer.remove(player);
      player.traverse((object) => {
        if (!object.isMesh) return;

        object.geometry.dispose();
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach((material) => {
          const map = material.map;
          const lightMap = material.lightMap;
          map?.dispose();
          if (lightMap && lightMap !== map) lightMap.dispose();
          material.dispose();
        });
      });
    };
  }, [camera, texture]);

  useFrame(() => {
    if (playerRef.current) {
      const { x, y } = cursorPosition;
      const angleY = Math.atan2(
        x,
        PLAYER_DISTANCE - HEAD_ANGLE_DISTANCE_OFFSET
      );
      const angleX = Math.atan2(
        y,
        PLAYER_DISTANCE - HEAD_ANGLE_DISTANCE_OFFSET
      );
      playerRef.current.rotation.y = angleY;
      playerRef.current.rotation.x = -angleX;
    }
  });

  return <group ref={playerRef} />;
}
